import {
  ref,
  watchEffect,
} from 'vue'
import type {
  Ref,
  ComponentPublicInstance,
} from 'vue'
import { debounce } from 'lodash'

type Fn = ReturnType<typeof debounce>

export type PopoverOption = {
  onBeforeOpen?: () => boolean
  onOpen?: () => void
  onClose?: () => void
  shouldCloseOnHoverLeave?: boolean
  trigger: string
  hideDelay?: number
}

type State = {
  toggle: (value?: boolean) => void
  close: () => void
  open: () => void
  handleClick: () => void
  handleHover: () => void
  handleCloseHover: () => void
  handleHoverLeave: () => void
  isShownState: Ref<boolean>
}

type globalOption = {
  targetRef: Ref<ComponentPublicInstance|HTMLElement|null>
  popperRef: Ref<any>
  toggle: (flag?: boolean) => void
  isShownState: Ref<boolean>
}

export function watchGlobalClick({
  targetRef,
  popperRef,
  toggle,
  isShownState,
} : globalOption) : void {
  function onBodyClick(e: Event) {
    if (!targetRef.value) {
      return
    }
    let el: HTMLElement
    if ('$el' in targetRef.value) {
      el = targetRef.value.$el
    } else {
      el = targetRef.value
    }
    if (el?.contains(e.target as Node)) {
      return
    }
    let popEl: HTMLElement
    if ('$el' in popperRef.value) {
      popEl = popperRef.value.$el
    } else {
      popEl = popperRef.value
    }
    if (popEl?.contains(e.target as Node)) {
      return
    }

    toggle(false)
  }

  watchEffect(onInvalidate => {
    onInvalidate(() => {
      document.body.removeEventListener('click', onBodyClick, false)
    })
    if (isShownState.value) {
      document.body.addEventListener('click', onBodyClick, false)
    } else {
      document.body.removeEventListener('click', onBodyClick, false)
    }
  })
}

export default function usePopover(
  props: PopoverOption,
) : State {
  const isShownState = ref(false)
  const ongoingDebounce = ref<null|Fn>(null)

  const open = () => {
    if (props.onBeforeOpen) {
      const prevent = props.onBeforeOpen()
      if (prevent) {
        return
      }
    }
    if (isShownState.value) {
      return
    }
    isShownState.value = true
    if (props.onOpen) {
      props.onOpen()
    }
  }

  const close = () => {
    if (!isShownState.value) {
      return
    }
    isShownState.value = false
    if (props.onClose) {
      props.onClose()
    }
  }

  const toggle = (flag?: boolean) => {
    let isOpen
    if (typeof flag === 'boolean') {
      isOpen = flag
    } else {
      isOpen = !isShownState.value
    }
    if (isOpen) {
      open()
    } else {
      close()
    }
  }

  const handleClick = () => {
    if (props.trigger !== 'click') {
      return
    }
    toggle()
  }

  const handleHover = () => {
    if (props.trigger !== 'hover') {
      return
    }
    if (ongoingDebounce.value) {
      ongoingDebounce.value.cancel()
      ongoingDebounce.value = null
    }
    open()
  }

  const handleCloseHover = () => {
    if (props.trigger !== 'hover') {
      return
    }
    ongoingDebounce.value = debounce(close, props.hideDelay || 50)
    ongoingDebounce.value()
  }

  const handleHoverLeave = () => {
    if (props.shouldCloseOnHoverLeave) {
      handleCloseHover()
    }
  }

  return {
    toggle,
    close,
    open,
    handleClick,
    handleHover,
    handleCloseHover,
    handleHoverLeave,
    isShownState,
  }
}
